import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock, Check, Truck, ClipboardCheck, Send } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
import { Skeleton } from './Skeleton';
import { getZoneForPosition } from '../data/deliveryZones';
import { useT } from '../utils/i18n';
import { saveOrderForTracking, buildShareLink, generateOrderNumber } from '../utils/orderTracking';
import { PostOrderDropSitePhoto } from './DropSitePhotoUpload';
import { AddressAutocomplete } from './AddressAutocomplete';
import { pushRecentOrder } from '../utils/recentOrders';
// Format a 10/11-digit phone string into (XXX) XXX-XXXX
function formatPhone(raw: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
// 50/50 Mix is classified HEAVY (12 yd full load) even though the name
// reads "mix" with topsoil — the blend (sand + topsoil) ships in the same
// 12-yd truckload as rock and aggregates.
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return (
    // Fill dirt removed — heavy base material, ships 12 yd/load.
    // 50/50 Mix removed — heavy blend (sand + topsoil) ships 12 yd/load.
    n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips')
  );
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee
  };
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    loadSavedCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo,
    promo,
    applyPromoCode,
    clearPromoCode
  } = useCart();
  const [promoInput, setPromoInput] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  // Auto-prefill promo input if user copied FIRST10 from the PromoBar
  useEffect(() => {
    if (isCartOpen && !promo.code) {
      try {
        const hint = sessionStorage.getItem('promo_hint');
        if (hint) {
          setPromoInput(hint);
          setShowPromoInput(true);
        }
      } catch {}
    }
  }, [isCartOpen, promo.code]);
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // True after the user tries to submit the cart form with missing/invalid
  // required fields. Switches the guidance ring (green, "fill this next") to
  // an error ring (red, "this is missing") on every invalid field, and stays
  // on until the user advances successfully.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  // Hydrate the local address input from context whenever the cart opens
  // (e.g. when an estimator flow pre-fills the address via setDeliveryInfo).
  // Only hydrate if the user hasn't typed something else in the drawer yet,
  // so we never stomp an in-progress edit.
  useEffect(() => {
    if (isCartOpen && deliveryAddress && !addressInput) {
      setAddressInput(deliveryAddress);
    }
  }, [isCartOpen, deliveryAddress]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  // dumpLocation dropdown was removed — the dump-location and special
  // instructions fields were merged into a single free-text
  // "Delivery Instructions" textarea (see the JSX below). All downstream
  // emails, SMS, and order tracking now use `instructions` as the single
  // source of truth for delivery directions.
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  // Tooltip explaining why same-day morning isn't guaranteed. Opens when
  // the customer clicks the "Not guaranteed" chip inside the time pill.
  // Kept separate from `showTimeDropdown` so the explanation can be read
  // without closing the time selector or vice-versa.
  const [showNotGuaranteedInfo, setShowNotGuaranteedInfo] = useState(false);
  // The dismissible "You don't need to be home" reassurance card was
  // replaced by an inline reassurance line above the Delivery Instructions
  // textarea, so no separate state or persistence is needed anymore.
  // Dump-location dropdown was removed in favor of a single free-text
  // "Delivery Instructions" textarea, so the outside-click handler that
  // previously closed the dropdown is no longer needed.
  // If a previously chosen time slot becomes invalid for the selected day
  // (e.g. user picks Afternoon, then switches to a Saturday — Sat ends 1 PM
  // so afternoon is closed), clear it so they re-select.
  useEffect(() => {
    if (!deliveryDate || !deliveryTime) return;
    const dow = new Date(deliveryDate + 'T12:00:00').getDay();
    if (dow === 0) {
      setDeliveryTime('');
    } else if (dow === 6 && deliveryTime === 'afternoon') {
      setDeliveryTime('');
    }
  }, [deliveryDate, deliveryTime]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [payOnDeliverySubMethod, setPayOnDeliverySubMethod] = useState<'cash' | 'card'>('cash');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderSummaryData, setOrderSummaryData] = useState<any>(null);
  // Surfaced when the order email/submission fails so the customer sees a
  // real error (and a way to retry / call) instead of a fake success screen.
  const [orderError, setOrderError] = useState<string>('');
  // New features state
  const [smsOptIn, setSmsOptIn] = useState(true);
  const totalDeliveryFee = deliveryFee * deliveryCount;
  // Material yards = what the customer actually needs (uses requestedQuantity
  // when the last load was rounded up to the 5yd minimum). Billed yards
  // (totalYards from context) is what we charge for. When they differ, we
  // surface both so the customer knows the extra 1yd is a billing minimum,
  // not extra material going to their site.
  const materialYards = items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity), 0);
  const yardsBilledExtra = totalYards - materialYards;
  // WA combined sales tax estimate for Pierce/King area (~10.1%) on materials only.
  // Delivery freight is generally non-taxable in WA when separately stated.
  const TAX_RATE = 0.101;
  // Saturday delivery surcharge — $40 per delivery when the order falls on a Saturday.
  // Multi-load orders pay the weekend surcharge on each load, matching how
  // the base delivery fee scales by deliveryCount.
  const SATURDAY_SURCHARGE_PER_LOAD = 40;
  const isSaturdayDelivery = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() === 6 : false;
  const saturdaySurcharge = isSaturdayDelivery ? SATURDAY_SURCHARGE_PER_LOAD * deliveryCount : 0;
  const discountedSubtotal = Math.max(0, totalPrice - promo.discount);
  // WA combined sales tax (~10.1%) on materials only. Delivery freight is
  // separately stated and remains non-taxable.
  const estimatedTax = Math.round(discountedSubtotal * TAX_RATE * 100) / 100;
  const finalTotal = discountedSubtotal + totalDeliveryFee + saturdaySurcharge + estimatedTax;
  // Same-day cutoff: 12 PM (noon) Mon–Sat. Before noon on a business day,
  // today is still selectable for afternoon / flexible delivery.
  // Otherwise the next business day (skipping Sunday).
  const minDate = (() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 6=Sat
    const hour = now.getHours();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    // Sunday: dispatch closed, earliest delivery is Monday
    if (dow === 0) {
      target.setDate(target.getDate() + 1);
    } else if (hour >= 12) {
      // After noon cutoff on a business day → next day
      target.setDate(target.getDate() + 1);
      // If next day is Sunday, push to Monday
      if (target.getDay() === 0) target.setDate(target.getDate() + 1);
    }
    return fmt(target);
  })();
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  const t = useT();
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    // If the address text matches the already-validated address (from the
    // Hero estimator or the Delivery Zones map page), trust the upstream
    // zone — don't re-geocode. Re-geocoding ambiguous addresses (e.g. a
    // street name without a city) can resolve to a different point and
    // produce a different zone than what the customer was originally
    // shown, which would silently change their delivery fee.
    if (addressInput.trim() === deliveryAddress.trim() && deliveryZone !== null) {
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        // viewbox keeps ambiguous addresses anchored to our service area
        // (roughly South King + Pierce County). bounded=1 forces results
        // to fall inside the box, preventing wrong-city resolutions.
        // Box: left,top,right,bottom = lon_min,lat_max,lon_max,lat_min
        const viewbox = '-122.75,47.75,-121.65,47.00';
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us&viewbox=${viewbox}&bounded=1`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address (include the city).');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, deliveryAddress, deliveryZone, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Block advancing to checkout if ANY required cart-step field is
    // missing. Delivery instructions in particular need to be enforced
    // here — if they're skipped, the customer reaches the checkout view
    // with a silently-disabled Place Order button and no obvious way
    // back to fix the missing field. Catching it here puts focus
    // directly on the unfilled input instead.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !deliveryTime || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      // Focus and scroll to the first missing field so the user is taken
      // directly to what they need to fix.
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !deliveryTime ? timeBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setHasAttemptedSubmit(false);
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation guard — the Place Order button's `disabled` attribute
    // only blocks mouse clicks. A keyboard Enter press inside any of
    // the text inputs can still trigger this form's onSubmit, which
    // previously let an order through with missing fields (most
    // commonly: empty delivery instructions). Re-check the same rules
    // the button uses, scroll the first missing field into view, and
    // bail out before any payment / order side-effects fire.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const isBark = isLightMaterial(item.product);
        const minChargeYards = 3;
        const smallLoadThreshold = isBark ? 12 : 10;
        // Partial last-load items in a split don't get the 3 yd min bump.
        const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
        const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
        const surcharge = (() => {
          if (billableYards >= smallLoadThreshold) return 1;
          return 1 + (smallLoadThreshold - billableYards) * 0.1;
        })();
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        let total = billableYards * effectivePrice;
        const fullLoad = isBark ? 15 : 12;
        const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isBark ? fullLoad : fullLoad * 2) ? 0.05 : 0;
        const hasBulk = bulkPct > 0;
        if (hasBulk) {
          total = Math.round(total * (1 - bulkPct));
        }
        const minNote = !isSplitDelivery && item.quantity < minChargeYards ? ` (${minChargeYards} yd min charge)` : '';
        const bulkNote = hasBulk ? ` (${Math.round(bulkPct * 100)}% bulk discount)` : '';
        // When the last load was rounded UP to the 5yd minimum, show the
        // actual material yards needed alongside what's billed so dispatch
        // and the customer both see the same breakdown.
        const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
        const yardsLabel = bumped ? `${item.requestedQuantity} yds material · billed as ${item.quantity} yds (3 yd order min)` : `${item.quantity} yds`;
        return `${item.product} (${item.category}) — ${yardsLabel}${minNote}${bulkNote} = ${total}`;
      }).join('\n');
      const deliveryDow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
      const isSatDelivery = deliveryDow === 6;
      const timeLabel = deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? isSatDelivery ? 'Afternoon (12-1 PM)' : 'Afternoon (12-4 PM)' : isSatDelivery ? 'Flexible (before 1 PM)' : 'Flexible';
      const deliveryFeeLabel = totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE';
      const promoLine = promo.code && promo.discount > 0 ? `\nPromo Code: ${promo.code} (10% off materials, −${promo.discount.toFixed(2)})` : '';
      const saturdayLine = saturdaySurcharge > 0 ? `\nSaturday Delivery Surcharge: ${saturdaySurcharge.toFixed(2)}${deliveryCount > 1 ? ` (${deliveryCount} × ${SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})` : ''}` : '';
      const subMethodLabel = payOnDeliverySubMethod.charAt(0).toUpperCase() + payOnDeliverySubMethod.slice(1);
      const paymentLabel = paymentMethod === 'card' ? 'Pay Online' : `Pay on Delivery (${subMethodLabel})`;
      // Order-source analytics — tag the email so you can see whether the
      // customer built this order in the Instant Estimator, the Shopping Cart
      // (Product page / Materials hub), or a mix of both.
      const sourceTags = new Set(items.map(it => (it as any).source || 'cart'));
      // If every item came from the Hero estimator, this is a pure
      // estimator handoff. If sources are mixed, fall through to "Mixed"
      // so dispatch can see the order was a combo flow.
      const isEstimatorHandoff = sourceTags.size === 1 && sourceTags.has('estimator');
      const isMixedSource = sourceTags.size > 1;
      const orderSourceLabel = isEstimatorHandoff ? 'Instant Estimator' : isMixedSource ? 'Mixed (Estimator + Cart)' : 'Direct Cart';
      // Yards breakdown for the email body — surfaces the 3yd order-min
      // bump when the customer requested less material than what gets
      // billed, so dispatch and the customer see the same numbers.
      const yardsLine = yardsBilledExtra > 0 ? `Material Yards: ${materialYards}\nBilled Yards: ${totalYards} (3 yd order min)` : `Total Yards: ${totalYards}`;
      const orderSummary = `Order Summary:\n${orderItems}\n\n${yardsLine}\nMaterials Total: ${totalPrice.toFixed(2)}${promoLine}\nDelivery Fee: ${deliveryFeeLabel}${saturdayLine}\nSales Tax (est. ${(TAX_RATE * 100).toFixed(1)}%): ${estimatedTax.toFixed(2)}\nOrder Total: ${finalTotal.toFixed(2)}\n\nOrder Source: ${orderSourceLabel}\n\nDelivery Address: ${addressInput}\nRequested Date: ${deliveryDate}\nTime Window: ${timeLabel}\nDelivery Instructions: ${instructions || 'Not specified'}\nPayment: ${paymentLabel}`;
      const phoneFormatted = (() => {
        const d = (phone || '').replace(/\D/g, '');
        const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
        return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
      })();
      // Build the order record up-front so we can include a live tracking
      // link inside the confirmation email body itself.
      // Order number format: 253-<CATEGORY>-<SEQ> (e.g. 253-BC-0042 for a
      // Base & Compaction order, 253-MX-0042 for a mixed-category cart).
      // Per-category sequence is maintained in localStorage.
      const orderNumber = generateOrderNumber(items);
      const trackedOrderPayload = {
        orderId: orderNumber,
        createdAt: Date.now(),
        address: addressInput,
        name,
        phone,
        email,
        date: deliveryDate,
        time: timeLabel,
        instructions,
        items: items.map(it => ({
          product: it.product,
          quantity: it.quantity,
          pricePerYard: it.pricePerYard
        })),
        totalDeliveryFee: deliveryFee,
        saturdaySurcharge: saturdaySurcharge || 0,
        estimatedTax,
        finalTotal
      };
      const trackLink = buildShareLink(trackedOrderPayload);
      const trackOrderSection = `TRACK YOUR ORDER\n\nOrder #: ${orderNumber}\n\nFollow your delivery in real time — confirmed, scheduled, dispatched, en route, delivered:\n${trackLink}\n\nBookmark this link or save this email. It works on any device, no login required.`;
      const whatsNext = `WHAT HAPPENS NEXT\n\n1. SMS Confirmation\n   A text confirmation will arrive at ${phoneFormatted} within a few minutes.\n\n2. Dispatch Review\n   Our team verifies your address, materials, and delivery window — usually within 1 business hour.\n\n3. Driver Dispatched\n   On delivery day, we'll text you a live tracking link the moment your driver is en route.\n\n4. Track Anytime\n   Check the status of your order at any time using your tracking link below.`;
      const howToPrepare = `HOW TO PREPARE FOR DELIVERY\n\n• Clear the dump zone — Mark a flat, accessible spot at least 10' wide x 20' long.\n• Driveway clearance — We need 12' of overhead clearance (watch for branches and wires).\n• Move vehicles & obstacles — Cars, trash bins, hoses, and toys should be clear of the path.\n• Surface protection — If dumping on a finished driveway, lay down plywood or a tarp to protect the surface.\n• Be reachable — Keep your phone nearby on delivery day in case the driver needs directions.\n• Have payment ready — If paying on delivery, please have cash, card, or check available.\n\nFull guide: https://253gravel.com/delivery-prep`;
      // Referral pitch — moved here from the site-wide announcement bar so
      // it reaches paying customers (where it actually has ROI) instead of
      // showing a discount to every visitor. The friend isn't promised a
      // dedicated referral discount (the public FIRST10 already gives
      // first-timers 10% off, so a "FRIEND10" code would be a meaningless
      // duplicate). The real referral incentive is the $25 credit to the
      // original customer — that's clear money in their pocket per
      // referred order, no double-promo confusion.
      const referralSection = `SHARE 253GRAVEL — EARN $50 PER REFERRAL\n\nKnow someone else who needs gravel, topsoil, or bark?\n\n  Have them mention your name when they book.\n  Once their order is delivered, we'll credit $50 to your next delivery — automatically.\n\nNo expiration. No minimum. No limit on referrals.`;
      // De-duplicated the "call us" line — the trailing footer already
      // tells the customer to call (206) 619-3742 with questions, so the
      // referral section doesn't need to repeat it.
      const fullEmailBody = `Hi ${name}! Thank you for your order request with 253 Gravel. We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\n----------\n\n${trackOrderSection}\n\n----------\n\n${whatsNext}\n\n----------\n\n${howToPrepare}\n\n----------\n\n${referralSection}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The 253 Gravel Team`;
      // Clear any prior submission error before re-trying.
      setOrderError('');
      const emailOk = await sendFormEmail({
        subject: '253Gravel — New Order Submission',
        to_name: name,
        from_name: '253 Gravel',
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Promo Code': promo.code && promo.discount > 0 ? `${promo.code} (−${promo.discount.toFixed(2)})` : 'None',
        'Delivery Fee': deliveryFeeLabel,
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        'Order Source': orderSourceLabel,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: fullEmailBody,
        message: fullEmailBody,
        'Order Number': orderNumber,
        'Track Order Link': trackLink,
        'Track Order Section': trackOrderSection,
        'What Happens Next': whatsNext,
        'How To Prepare': howToPrepare,
        'Delivery Date': deliveryDate,
        'Delivery Time': timeLabel,
        'Delivery Instructions': instructions || 'Not specified',
        'Drop-Site Photo': 'Pending — customer can upload from order success screen',
        'Payment Method': paymentLabel
      });
      // CRITICAL: only transition to the success view if EmailJS actually
      // accepted the order. Previously the code optimistically flipped
      // view='success' regardless of email outcome, which made it look
      // like the order went through even when dispatch never received it.
      if (!emailOk) {
        setOrderError("We couldn't send your order to dispatch just now. Please try again, or call us at (206) 619-3742 and we'll take it over the phone.");
        setIsProcessingPayment(false);
        return;
      }
      setOrderSummaryData({
        items: [...items],
        totalYards,
        materialYards,
        yardsBilledExtra,
        totalPrice,
        deliveryFeeLabel,
        saturdaySurcharge,
        estimatedTax,
        finalTotal,
        addressInput,
        deliveryDate,
        timeLabel,
        name,
        phone,
        email,
        orderNumber
      });
      // Append to the recent-orders list FIRST (up to 3, de-duped) so the
      // hero "Reorder last delivery" drawer can offer a choice between the
      // customer's standard loads. This must run BEFORE we overwrite the
      // single-order key below — pushRecentOrder seeds from that key, so
      // overwriting it first would clobber the previous order and we'd only
      // ever keep the latest one.
      pushRecentOrder({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      });
      // Save the single most-recent order for backward-compat (other
      // surfaces and older devices still read this key).
      localStorage.setItem('253gravel_last_order', JSON.stringify({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      }));
      // Save a complete tracked-order record so the customer can track it.
      try {
        saveOrderForTracking(trackedOrderPayload);
      } catch (e) {
        console.error('Could not save tracked order', e);
      }
      // Email accepted AND the success snapshot is staged — now it's safe
      // to clear the live cart and show the confirmation screen. Doing
      // both here (instead of waiting for handleCloseSuccess) means the
      // cart stays empty no matter HOW the customer dismisses the
      // success view (Done, X, backdrop tap, navigating away).
      setView('success');
      clearCart();
    } catch (err) {
      console.error('Order submission error:', err);
      setOrderError('Something went wrong placing your order. Please try again, or call us at (206) 619-3742.');
    } finally {
      setIsProcessingPayment(false);
    }
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    // Note: the old setDumpLocation('') reset call lived here. The
    // dumpLocation state was removed when the dump-location dropdown
    // got merged into the free-text Delivery Instructions textarea
    // (see the comment near the top of this file). Leaving the setter
    // call behind threw "setDumpLocation is not defined" the moment
    // the customer closed the success screen.
    clearCart();
    closeCart();
  };
  const inputClass = 'w-full px-4 py-3.5 bg-[#f5f5f5] dark:bg-white/10 backdrop-blur-sm border border-[#e5e5e5] dark:border-white/10 rounded-xl text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-stone-400 focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:shadow-[0_0_15px_rgba(255,140,50,0.15)] transition-all text-base font-medium';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  const handleReorder = () => {
    const lastOrderStr = localStorage.getItem('253gravel_last_order');
    if (lastOrderStr) {
      const lastOrder = JSON.parse(lastOrderStr);
      if (lastOrder.items && lastOrder.items.length > 0) {
        loadSavedCart(lastOrder.items);
        setAddressInput(lastOrder.addressInput || '');
        setName(lastOrder.name || '');
        setPhone(lastOrder.phone || '');
        setEmail(lastOrder.email || '');
      }
    }
  };
  const getDeliveryDayText = () => {
    if (!deliveryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delDate = new Date(deliveryDate + 'T00:00:00');
    const diffTime = delDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Same-day delivery!';
    if (diffDays === 1) return 'Delivering tomorrow!';
    return `Delivering in ${diffDays} days`;
  };
  return <AnimatePresence data-id="element-236">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-stone-900/40 dark:bg-black/70 backdrop-blur-md z-[60]" data-id="element-237" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-238">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className={
        // Sage tint for BOTH steps now. Previously the cart
        // step used a neutral cream surface and only the
        // checkout step had the sage treatment; that made the
        // drawer "change color" mid-flow. Keeping the same
        // surface throughout reads as a single, calm container.
        'w-full max-w-5xl backdrop-blur-xl flex flex-col rounded-2xl max-h-[90dvh] overflow-hidden pointer-events-auto transition-colors duration-300 border-2 bg-[#e8ede4] dark:bg-[#1b2823]/95 border-[#d4ddcf] dark:border-[#3a4e3a]/60 shadow-[0_2px_8px_rgba(110,140,106,0.12),0_24px_70px_-12px_rgba(110,140,106,0.28)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.4),0_24px_70px_-12px_rgba(110,140,106,0.35)]'} role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-239">
              {/* Header */}
              <div className={'flex items-center justify-between px-8 py-6 border-b shrink-0 transition-colors duration-300 ' + (view === 'checkout' ? 'border-[#6e8c6a]/30 dark:border-[#6e8c6a]/30 bg-gradient-to-b from-[#6e8c6a]/[0.10] to-transparent dark:from-[#6e8c6a]/[0.15] dark:to-transparent' : 'border-[#e5e5e5] dark:border-white/10 bg-gradient-to-b from-[#f5f5f5] to-transparent dark:from-white/5 dark:to-transparent')} data-id="element-240">
                <div className="flex items-center space-x-4" data-id="element-241">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-[#059F40] hover:text-[#0C2809] dark:text-[#22c95c] dark:hover:text-white transition-colors rounded-lg hover:bg-[#059F40]/15 mr-1" aria-label="Back to cart" data-id="element-242">
                      <ArrowLeft className="w-6 h-6" data-id="element-243" />
                    </button>}
                  <Truck className={'w-7 h-7 transition-colors ' + (view === 'checkout' ? 'text-[#059F40]' : 'text-brand-charcoal dark:text-white')} data-id="element-244" />
                  <h2 id="cart-drawer-title" className={'font-heading text-xl font-bold transition-colors ' + (view === 'checkout' ? 'text-[#0C2809] dark:text-white' : 'text-brand-charcoal dark:text-white')} data-id="element-245">
                    {view === 'cart' ? t('cart.title') : view === 'checkout' ? t('cart.checkout') : t('cart.orderConfirmed')}
                  </h2>
                  {view === 'checkout' && <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#059F40]/15 border border-[#059F40]/35 text-[10px] font-bold uppercase tracking-wider text-[#059F40] dark:text-[#22c95c]" data-id="element-246">
                      <Lock className="w-3 h-3" data-id="element-247" />
                      Secure Checkout
                    </span>}
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close cart" data-id="element-248">
                  <X className="w-6 h-6" data-id="element-249" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-250">
                  {/* Sticky Progress Stepper — only when there are items */}
                  {items.length > 0 && <div className="sticky top-0 z-[55] bg-[#fcfcfc]/85 dark:bg-[#2a2a2a]/85 backdrop-blur-md border-b border-[#e5e5e5] dark:border-white/5 px-6 py-2.5" data-id="element-251">
                      <div className="flex items-center justify-between max-w-full" data-id="element-252">
                        {[{
                  id: 'address',
                  label: 'Address',
                  isComplete: isAddressValid,
                  isActive: !isAddressValid
                }, {
                  id: 'name',
                  label: 'Name',
                  isComplete: isNameValid,
                  isActive: isAddressValid && !isNameValid
                }, {
                  id: 'phone',
                  label: 'Phone',
                  isComplete: isPhoneValid,
                  isActive: isAddressValid && isNameValid && !isPhoneValid
                }, {
                  id: 'email',
                  label: 'Email',
                  isComplete: isEmailValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && !isEmailValid
                }, {
                  id: 'date',
                  label: 'Date',
                  isComplete: isDateValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid
                }, {
                  id: 'time',
                  label: 'Time',
                  isComplete: !!deliveryTime,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime
                }].map((step, idx, arr) => <Fragment key={step.id} data-id="element-253">
                            <div className="flex items-center gap-1.5 relative z-10 shrink-0" data-id="element-254">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300 ${step.isComplete ? 'bg-green-700 text-white' : step.isActive ? 'bg-brand-orange/90 text-white ring-2 ring-brand-orange/15' : 'bg-stone-200 dark:bg-white/10 text-stone-400 dark:text-stone-500'}`} data-id="element-255">
                                {step.isComplete ? <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-256" /> : idx + 1}
                              </div>
                              <span className={`text-[10px] font-semibold tracking-wide hidden sm:block ${step.isComplete ? 'text-green-700 dark:text-green-500' : step.isActive ? 'text-brand-orange/90' : 'text-stone-400 dark:text-stone-500'}`} data-id="element-257">
                                {step.label}
                              </span>
                            </div>
                            {idx < arr.length - 1 && <div className="flex-1 h-px mx-1.5 bg-stone-200 dark:bg-white/10 relative" data-id="element-258">
                                <div className="absolute top-0 left-0 h-full bg-green-700 transition-all duration-500" style={{
                      width: step.isComplete ? '100%' : '0%'
                    }} data-id="element-259" />
                              </div>}
                          </Fragment>)}
                      </div>
                    </div>}
                  {/* Items */}
                  <div className="p-8" data-id="element-260">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-261">
                        <div className="w-28 h-28 bg-white dark:bg-white/5 rounded-full flex items-center justify-center border border-[#e5e5e5] dark:border-white/10 shadow-inner" data-id="element-262">
                          <Truck className="w-12 h-12 text-brand-muted opacity-50" data-id="element-263" />
                        </div>
                        <div data-id="element-264">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-265">
                            Your truck is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-266">
                            Looks like you haven't added any materials to your
                            truck yet.
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 w-full max-w-[280px]" data-id="element-267">
                          <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm w-full" data-id="element-268">
                            Browse Materials
                          </button>
                          {localStorage.getItem('253gravel_last_order') && <button type="button" onClick={handleReorder} className="bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange border border-brand-orange/30 px-10 py-4 rounded-xl font-bold text-lg transition-colors w-full" data-id="element-269">
                              Reorder Last Order
                            </button>}
                        </div>
                      </div> : <div className="space-y-5" data-id="element-270">
                        {items.map(item => <div key={item.id} className="flex flex-col sm:flex-row gap-5 p-5 bg-white dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/10 shadow-sm dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] overflow-hidden" data-id="element-271">
                            <div className="flex-1" data-id="element-272">
                              <p className="text-sm font-bold text-brand-muted dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-273">
                                {item.category}
                              </p>
                              <h4 className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white mb-1.5" data-id="element-274">
                                {item.product}
                              </h4>
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 3;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
                      const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
                      const surcharge = (() => {
                        if (billableYards >= smallLoadThreshold) return 1;
                        return 1 + (smallLoadThreshold - billableYards) * 0.1;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      const fullLoadDisc = isLight ? 15 : 12;
                      const bulkPct = item.quantity >= fullLoadDisc * 4 ? 0.1 : item.quantity >= (isLight ? fullLoadDisc : fullLoadDisc * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct > 0;
                      const displayPrice = hasBulk ? Math.ceil(effectivePrice * (1 - bulkPct)) : effectivePrice;
                      /* Per-yard row. Uses the same
                      amber/green pill styling as the
                      Product page price card so the cart
                      reads consistently:
                      - Bulk discount tier → green pill
                      "X% BULK DISCOUNT" (paying less).
                      - Below small-load threshold → amber
                      pill "SMALL-LOAD RATE" (paying
                      more).
                      - 3-yd min charge → unchanged amber
                      text (rare edge case). */
                      return <div className="flex items-center flex-wrap gap-x-2 gap-y-1" data-id="element-275">
                                    <p className="text-base text-brand-muted dark:text-stone-200 font-medium" data-id="element-276">
                                      ${displayPrice}{' '}
                                      <span className="text-stone-500 dark:text-stone-400" data-id="element-277">
                                        / yard
                                      </span>
                                    </p>
                                    {hasBulk && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#059F40] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-278">
                                        {Math.round(bulkPct * 100)}% Bulk
                                        Discount
                                      </span>}
                                    {!hasBulk && !isSplitDelivery && item.quantity < minChargeYards && <span className="text-xs text-brand-orange" data-id="element-279">
                                          ({minChargeYards} yd min charge)
                                        </span>}
                                    {!hasBulk && billableYards < smallLoadThreshold && (isSplitDelivery || item.quantity >= minChargeYards) && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#EAB308] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-280">
                                          Small-load rate
                                        </span>}
                                  </div>;
                    })()}
                            </div>
                            <div className="flex flex-col gap-2 min-w-0" data-id="element-281">
                              {/* Price row */}
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 3;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
                      const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
                      const surcharge = (() => {
                        if (billableYards >= smallLoadThreshold) return 1;
                        return 1 + (smallLoadThreshold - billableYards) * 0.1;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      let itemTotal = billableYards * effectivePrice;
                      const baseProduct = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
                      const aggYards = items.filter(i => baseProduct(i.product) === baseProduct(item.product)).reduce((s, i) => s + i.quantity, 0);
                      const fullLoad = isLight ? 15 : 12;
                      const bulkPct2 = aggYards >= fullLoad * 4 ? 0.1 : aggYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct2 > 0;
                      if (hasBulk) {
                        itemTotal = Math.round(itemTotal * (1 - bulkPct2));
                      }
                      /* Total row. Pill above already
                      conveys the bulk discount, so the
                      secondary tag here is replaced with
                      a clean "$X savings" copy in accent
                      green — mirrors the Product page. */
                      const savingsAmount = hasBulk ? Math.round(billableYards * effectivePrice * bulkPct2) : 0;
                      return <div className="flex items-baseline gap-2 flex-wrap" data-id="element-282">
                                    <p className="font-heading font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-283">
                                      ${itemTotal}
                                    </p>
                                    {hasBulk && savingsAmount > 0 && <span className="text-xs text-[#059F40] font-semibold" data-id="element-284">
                                        ${savingsAmount} savings
                                      </span>}
                                  </div>;
                    })()}

                              {/* Quantity + Delete row */}
                              <div className="flex items-center gap-3" data-id="element-285">
                                {(() => {
                        const isBarkMulch = isLightMaterial(item.product);
                        const fullLoad = isBarkMulch ? 15 : 12;
                        const isFullLoadMultiple = item.quantity % fullLoad === 0;
                        const loadCount = item.quantity / fullLoad;
                        const handleMinus = () => {
                          if (isFullLoadMultiple && loadCount > 1) {
                            updateQuantity(item.id, item.quantity - fullLoad);
                          } else {
                            updateQuantity(item.id, item.quantity - 1);
                          }
                        };
                        const maxQty = isBarkMulch ? 60 : 48;
                        const handlePlus = () => {
                          if (item.quantity >= maxQty) return;
                          if (isFullLoadMultiple) {
                            updateQuantity(item.id, Math.min(item.quantity + fullLoad, maxQty));
                          } else {
                            updateQuantity(item.id, Math.min(item.quantity + 1, maxQty));
                          }
                        };
                        const tonsEstimate = (item.quantity * getDensity(item.product)).toFixed(1);
                        return <div className="flex flex-col items-center gap-1" data-id="element-286">
                                      <div className="flex items-center bg-white dark:bg-white/10 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-lg overflow-hidden min-w-[120px] sm:min-w-[140px]" data-id="element-287">
                                        <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Decrease quantity" data-id="element-288">
                                          <Minus className="w-5 h-5" data-id="element-289" />
                                        </button>
                                        <div className="flex items-center justify-center py-1.5 flex-1 whitespace-nowrap gap-1.5" data-id="element-290">
                                          <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-291">
                                            {isFullLoadMultiple ? loadCount : item.quantity}
                                          </span>
                                          <span className="text-[10px] text-brand-muted dark:text-stone-300 uppercase tracking-wider font-bold" data-id="element-292">
                                            {isFullLoadMultiple ? loadCount === 1 ? 'Load' : 'Loads' : 'yd'}
                                          </span>
                                        </div>
                                        <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Increase quantity" data-id="element-293">
                                          <Plus className="w-5 h-5" data-id="element-294" />
                                        </button>
                                      </div>
                                      <span className="text-xs text-stone-400 dark:text-stone-400 font-medium" data-id="element-295">
                                        {isFullLoadMultiple ? `${item.quantity} yds` : null}
                                        {isFullLoadMultiple ? ' · ' : ''}≈{' '}
                                        {tonsEstimate} tons
                                      </span>
                                    </div>;
                      })()}

                                {/* Delete button */}
                                <button onClick={() => removeFromCart(item.id)} className="p-2 text-brand-muted hover:text-brand-orange transition-colors shrink-0" title="Remove item" aria-label={`Remove ${item.product} from cart`} data-id="element-296">
                                  <Trash2 className="w-5 h-5" data-id="element-297" />
                                </button>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Contractor Prompt */}
                  {(() => {
              const productYards: Record<string, number> = {};
              const baseName = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
              for (const item of items) {
                const base = baseName(item.product);
                productYards[base] = (productYards[base] || 0) + item.quantity;
              }
              return Object.values(productYards).some(y => y > 24);
            })() && <div className="mx-8 mb-2 p-4 bg-brand-orange/10 dark:bg-brand-orange/10 border border-brand-orange/30 rounded-xl flex items-start gap-3" data-id="element-298">
                      <Info className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-299" />
                      <div data-id="element-300">
                        <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-301">
                          Are you a contractor?
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-302">
                          Get priority scheduling, volume pricing, and dedicated
                          support.{' '}
                          <a href="/subcontractors" className="text-brand-orange font-bold underline hover:text-brand-orange/80 transition-colors" onClick={() => closeCart()} data-id="element-303">
                            Sign up on our Contractors page →
                          </a>
                        </p>
                      </div>
                    </div>}

                  {/* Delivery Details */}
                  {items.length > 0 && <div className={`px-8 pb-8 pt-8 ${showCalendar ? 'relative z-[200]' : ''}`} data-id="element-304">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-stone-500 tracking-tight mb-5" data-id="element-305">
                        Delivery Details
                      </h3>
                      <div className="space-y-4 bg-white dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] p-5" data-id="element-306">
                        {/* Address */}
                        <div data-id="element-307">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-308">
                            <span className="flex items-center" data-id="element-309">
                              <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-310" />
                              Delivery Address
                            </span>
                          </label>
                          <AddressAutocomplete value={addressInput} onChange={setAddressInput} placeholder="123 Main St, Tacoma, WA" inputClassName={`${inputClass} ${hasAttemptedSubmit && !isAddressValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required variant="light" data-id="element-311" />
                          <div aria-live="polite" className="mt-1.5" data-id="element-312">
                            {isCalculatingDelivery && <Skeleton className="h-5 w-[60%] rounded" data-id="element-313" />}
                            {addressError && !isCalculatingDelivery && <p className="text-sm text-red-500 font-medium" data-id="element-314">
                                {addressError}
                              </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 flex flex-wrap items-center gap-x-1.5 font-medium" data-id="element-315">
                                  <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-316" />
                                  <span data-id="element-317">
                                    Zone {deliveryZone}{' '}
                                    <span className="font-bold" data-id="element-318">
                                      {deliveryFee === 0 ? '(Free)' : deliveryCount > 1 ? `(+${totalDeliveryFee.toFixed(0)} · ${deliveryCount} loads)` : `(+${deliveryFee})`}
                                    </span>
                                  </span>
                                  <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors whitespace-nowrap" data-id="element-319">
                                    View zones
                                  </a>
                                </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange flex items-center font-medium" data-id="element-320">
                                  <Info className="w-4 h-4 mr-1" data-id="element-321" />
                                  Outside standard zones. We'll call with a
                                  custom quote.
                                </p>}
                          </div>
                        </div>

                        {/* Name */}
                        <div data-id="element-322">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-323">
                            <FileText className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-324" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${hasAttemptedSubmit && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={hasAttemptedSubmit && !isNameValid} data-id="element-325" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-326">
                          <div data-id="element-327">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-328">
                              <Phone className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-329" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${hasAttemptedSubmit && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isPhoneValid && phone.length > 0 || hasAttemptedSubmit && !isPhoneValid} data-id="element-330" />
                          </div>
                          <div data-id="element-331">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-332">
                              <Mail className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-333" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${hasAttemptedSubmit && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isEmailValid && email.length > 0 || hasAttemptedSubmit && !isEmailValid} data-id="element-334" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-335">
                          <div className={`relative ${showCalendar ? 'z-[100]' : ''}`} data-id="element-336">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-337">
                              <CalendarDays className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-338" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} data-id="element-339">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-340">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-341" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-342">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-[#e5e5e5] dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-5" data-id="element-343">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-344">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-345">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-346" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-347">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-348">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-349" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-350">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-351">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-352">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-353" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                const cellDow = new Date(dateStr + 'T12:00:00').getDay();
                                const isSat = cellDow === 6;
                                // Dispatch is closed on
                                // Sundays — block the
                                // entire day at the picker
                                // so customers don't pick
                                // a date, see every time
                                // window labeled CLOSED,
                                // and walk away frustrated.
                                const isSun = cellDow === 0;
                                const isDisabled = isPast || isSun;
                                return <button key={dateStr} type="button" disabled={isDisabled} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} title={isSun ? 'Closed Sundays — please choose another day' : isSat && !isPast ? 'Saturday delivery: +$40 weekend surcharge' : undefined} className={`relative text-base font-bold py-2 rounded-xl transition-colors ${isDisabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed line-through decoration-stone-300 dark:decoration-stone-600/60' : isSelected ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : isToday ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-354">
                                                {day}
                                                {isSat && !isPast && <span className={`absolute -top-0.5 -right-0.5 text-[8px] font-extrabold leading-none px-1 py-0.5 rounded ${isSelected ? 'bg-white/30 text-white' : 'bg-brand-orange/20 text-brand-orange'}`} data-id="element-355">
                                                    +$40
                                                  </span>}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className={`relative ${showTimeDropdown ? 'z-[100]' : ''}`} data-id="element-356">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-357">
                              <Clock className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-358" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-359" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} data-id="element-360">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white flex items-center gap-2 min-w-0' : 'text-stone-400'} data-id="element-361">
                                <span className="truncate" data-id="element-362">
                                  {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                                </span>
                              </span>
                              {/* Right-side Clock icon. When the user has
                               picked the 7–11 AM window on a same-day order,
                               the Clock glows amber and becomes its own tap
                               target that opens the "not guaranteed"
                               explanation popover (stopPropagation so the
                               outer time-selector button doesn't toggle the
                               dropdown). Otherwise it's just the field's
                               passive affordance. */}
                              {(() => {
                        const d = new Date();
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        const todayStr = `${y}-${m}-${day}`;
                        const isMorningToday = deliveryTime === 'morning' && deliveryDate === todayStr;
                        if (isMorningToday) {
                          return <span role="button" tabIndex={0} aria-label="Same-day morning is not guaranteed. Tap for details." onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowNotGuaranteedInfo(v => !v);
                          }} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              e.preventDefault();
                              setShowNotGuaranteedInfo(v => !v);
                            }
                          }} className="shrink-0 inline-flex items-center justify-center rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/60" data-id="element-363">
                                      <Clock className="w-5 h-5 text-amber-500 dark:text-amber-400 shrink-0" style={{
                              filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.6))'
                            }} data-id="element-364" />
                                    </span>;
                        }
                        return <Clock className="w-5 h-5 text-stone-300 shrink-0" data-id="element-365" />;
                      })()}
                            </button>
                            <AnimatePresence data-id="element-366">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 right-0 w-[320px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-[#e5e5e5] dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-3 space-y-1" data-id="element-367">
                                  {(() => {
                          // Gate windows by the selected day's dispatch hours.
                          // Mon–Fri 6a–5p, Sat ends at 1 PM, Sun closed.
                          const dow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
                          const isSat = dow === 6;
                          const isSun = dow === 0;
                          const noDate = !deliveryDate;
                          // Detect same-day: morning windows can't be
                          // guaranteed since trucks are already routed.
                          const todayStr = (() => {
                            const d = new Date();
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })();
                          const isSameDay = deliveryDate === todayStr;
                          // After 10 AM the morning window
                          // (7–11 AM) is physically impossible
                          // for same-day — by then dispatch is
                          // halfway through the morning route
                          // and there's no truck left that can
                          // still hit a 7–11 slot today. Block
                          // the option entirely so customers
                          // don't pick it and then have to be
                          // told no.
                          const currentHour = new Date().getHours();
                          const morningTooLate = isSameDay && currentHour >= 10;
                          const opts = [{
                            value: 'morning',
                            label: morningTooLate ? 'Morning — too late for today' : 'Morning (7–11 AM)',
                            disabled: isSun || morningTooLate,
                            note: isSameDay && !morningTooLate ? 'Best effort — not guaranteed' : null
                          }, {
                            value: 'afternoon',
                            // Saturday: dispatch ends at 1 PM, so afternoon is unavailable
                            label: isSat ? 'Afternoon — Sat ends 1 PM' : 'Afternoon (12–4 PM)',
                            disabled: isSun || isSat,
                            note: isSameDay && !isSat ? 'Recommended for same-day' : null
                          }, {
                            value: 'flexible',
                            label: isSat ? 'Flexible (before 1 PM)' : 'Flexible',
                            disabled: isSun,
                            note: isSameDay ? 'Best chance same-day' : null
                          }];
                          if (noDate) {
                            return <p className="text-xs text-stone-700 dark:text-stone-300 px-3 py-3 font-medium" data-id="element-368">
                                          Pick a date first.
                                        </p>;
                          }
                          return opts.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
                            if (opt.disabled) return;
                            setDeliveryTime(opt.value);
                            setShowTimeDropdown(false);
                            // Continue the auto-advance
                            // chain — once a time window
                            // is picked, take the customer
                            // straight to the delivery
                            // instructions textarea (the
                            // last required field).
                            setTimeout(() => {
                              instructionsRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                              });
                              instructionsRef.current?.focus({
                                preventScroll: true
                              });
                            }, 200);
                          }} className={`w-full text-right px-4 py-3 rounded-xl text-base font-bold transition-colors flex items-center justify-between flex-row-reverse ${opt.disabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : deliveryTime === opt.value ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-369">
                                        <div className="flex flex-col items-end" data-id="element-370">
                                          <span data-id="element-371">{opt.label}</span>
                                          {opt.note && !opt.disabled && <span className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${opt.value === 'morning' ? deliveryTime === opt.value ? 'text-white/85' : 'text-amber-600 dark:text-amber-400' : deliveryTime === opt.value ? 'text-white/85' : 'text-green-600 dark:text-green-400'}`} data-id="element-372">
                                              {opt.note}
                                            </span>}
                                        </div>
                                        {opt.disabled && <span className="text-[10px] uppercase tracking-wider font-bold text-stone-400" data-id="element-373">
                                            Closed
                                          </span>}
                                      </button>);
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                            {/* Detailed "why isn't same-day morning guaranteed?"
                             popover — opens when the chip in the time pill
                             is clicked. Anchored just below the pill so the
                             customer can read the explanation without
                             losing context. A semi-transparent backdrop
                             catches outside clicks to dismiss. */}
                            <AnimatePresence data-id="element-374">
                              {showNotGuaranteedInfo && <>
                                  {/* Invisible click-outside catcher */}
                                  <div className="fixed inset-0 z-[110]" onClick={() => setShowNotGuaranteedInfo(false)} data-id="element-375" />
                                  <motion.div initial={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} transition={{
                          duration: 0.18
                        }} role="dialog" aria-modal="true" aria-labelledby="not-guaranteed-title" className="absolute z-[120] top-full mt-2 right-0 w-[340px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-amber-500/40 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] px-6 py-5" data-id="element-376">
                                    <div className="flex items-start gap-3 mb-3" data-id="element-377">
                                      <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0" data-id="element-378">
                                        <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" data-id="element-379" />
                                      </div>
                                      <div className="flex-1 min-w-0" data-id="element-380">
                                        <h4 id="not-guaranteed-title" className="text-sm font-extrabold text-stone-900 dark:text-white leading-tight" data-id="element-381">
                                          Why isn't same-day morning guaranteed?
                                        </h4>
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mt-0.5" data-id="element-382">
                                          7–11 AM same-day only
                                        </p>
                                      </div>
                                      <button type="button" onClick={() => setShowNotGuaranteedInfo(false)} aria-label="Close" className="shrink-0 -mt-1 -mr-1 p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-id="element-383">
                                        <X className="w-4 h-4" data-id="element-384" />
                                      </button>
                                    </div>

                                    <div className="space-y-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed" data-id="element-385">
                                      <p data-id="element-386">
                                        Our morning trucks are dispatched
                                        starting at 6 AM, and most are already
                                        loaded and routed for previously booked
                                        deliveries before the day even starts.
                                      </p>
                                      <p data-id="element-387">
                                        When you book a morning slot{' '}
                                        <span className="font-bold" data-id="element-388">
                                          on the same day
                                        </span>
                                        , we squeeze you into open gaps if we
                                        can — but we can't promise the window
                                        because the schedule is already in
                                        motion.
                                      </p>
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-389">
                                      <button type="button" onClick={() => {
                              setDeliveryTime('flexible');
                              setShowNotGuaranteedInfo(false);
                              // Match the main time-picker:
                              // hand the customer off to the
                              // instructions field once a
                              // time choice is made.
                              setTimeout(() => {
                                instructionsRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center'
                                });
                                instructionsRef.current?.focus({
                                  preventScroll: true
                                });
                              }, 200);
                            }} className="px-3 py-2 rounded-lg bg-[#059F40] hover:bg-[#17A347] text-white text-xs font-bold transition-colors" data-id="element-390">
                                        Switch to Flexible
                                      </button>
                                      <a href="tel:206-619-3742" className="px-3 py-2 rounded-lg border border-stone-300 dark:border-white/15 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 text-stone-800 dark:text-white text-xs font-bold transition-colors text-center flex items-center justify-center gap-1.5" data-id="element-391">
                                        <Phone className="w-3.5 h-3.5" data-id="element-392" />
                                        Call to confirm
                                      </a>
                                    </div>
                                  </motion.div>
                                </>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same-day morning warning is now displayed inline
                         inside the time selector pill itself — see the
                         "Not guaranteed" chip rendered next to "Morning
                         (7–11 AM)" above. The standalone amber banner that
                         used to sit here was removed to declutter the
                         checkout form. */}

                        {/* Removed: standalone "Delivering tomorrow!"
                         countdown pill and the dismissible "You don't
                         need to be home" green card. Both
                         added clutter without enough informational
                         value — the date field already communicates
                         when delivery lands, and the dump-location
                         picker directly below makes the no-home-needed
                         workflow self-evident. Customer-feedback driven
                         decluttering pass. */}

                        {/* Delivery Instructions (required) — single
                         free-text field that replaces both the older
                         Dump Location dropdown and the Additional Notes
                         textarea. Customers describe the drop spot and
                         any access notes in their own words, and the
                         inline reassurance row above reminds them they
                         don't need to be home — the most common
                         checkout hesitation.
                         Drop-site photo lives on the SUCCESS screen
                         post-submit so the form stays short. */}
                        <div data-id="element-393">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-394">
                            <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-395" />
                            Delivery Instructions
                            <span className="ml-1.5 text-amber-500 dark:text-amber-400" aria-hidden="true" data-id="element-396">
                              *
                            </span>
                            <span className="sr-only" data-id="element-397">required</span>
                          </label>

                          {/* "You don't need to be home" reassurance — softer
                           treatment than the old dismissible card. Lives
                           inline above the textarea so the message lands
                           right where the customer is wondering whether
                           they need to be there to direct the driver. */}
                          <div className="flex items-start gap-2 mb-2.5 text-xs text-brand-muted dark:text-stone-400 leading-relaxed" data-id="element-398">
                            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#059F40] dark:text-[#22c95c]" strokeWidth={3} data-id="element-399" />
                            <p data-id="element-400">
                              <span className="font-bold dark:text-stone-200 text-[#E6AE60]" data-id="element-401">
                                You don't need to be home.
                              </span>{' '}
                              Just tell the driver where to dump the load and
                              note any access details — driveway side, gate
                              codes, dogs to avoid, etc.
                            </p>
                          </div>

                          <textarea ref={instructionsRef} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Left side of driveway near the fence. Gate code 1234. Friendly dog in backyard." rows={4} className={`${inputClass} resize-none ${hasAttemptedSubmit && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} aria-required="true" aria-invalid={hasAttemptedSubmit && !instructions.trim()} data-id="element-402" />

                          {/* Property & Vehicle Safety callout — appears once
                           the customer has entered instructions so it reads
                           as a "before you submit" reminder. Same copy that
                           lived inside the deleted DumpPointMap; surfaces
                           the constraint at the moment the driver's path
                           is being described, not after the order ships. */}
                          <AnimatePresence data-id="element-403">
                            {instructions.trim().length > 0 && <motion.div initial={{
                      opacity: 0,
                      height: 0,
                      marginTop: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto',
                      marginTop: 12
                    }} exit={{
                      opacity: 0,
                      height: 0,
                      marginTop: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-404">
                                <div className="flex items-start gap-2.5 p-3 rounded-sm bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30" data-id="element-405">
                                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" data-id="element-406" />
                                  <div data-id="element-407">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1" data-id="element-408">
                                      Property & Vehicle Safety
                                    </h5>
                                    <p className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed" data-id="element-409">
                                      We cannot drive across irrigated, wet,
                                      frozen, or landscaped areas to reach the
                                      dump spot. Please make sure your
                                      instructions keep our truck on gravel or
                                      paved surfaces.
                                    </p>
                                  </div>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className={`px-8 pb-8 border-t border-white/20 dark:border-white/10 pt-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent ${showCalendar ? 'relative z-[50]' : ''}`} data-id="element-410">
                      <div className="space-y-3 mb-6" data-id="element-411">
                        <div className="flex justify-between items-end" data-id="element-412">
                          <span className="text-brand-muted dark:text-stone-300 font-bold text-base" data-id="element-413">
                            Materials ({materialYards} yards
                            {yardsBilledExtra > 0 && <span className="font-medium text-amber-600 dark:text-amber-400" data-id="element-414">
                                {' '}
                                · billed as {totalYards} yd (3 yd order min)
                              </span>}{' '}
                            · ≈{' '}
                            {items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity) * getDensity(item.product), 0).toFixed(1)}{' '}
                            tons
                            {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            )
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-415">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>

                        {/* Promo Code */}
                        <div className="py-1" data-id="element-416">
                          {promo.code ? <div className="flex justify-between items-center bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2" data-id="element-417">
                              <div className="flex items-center gap-2" data-id="element-418">
                                <Check className="w-4 h-4 text-amber-500 dark:text-amber-400" data-id="element-419" />
                                <span className="text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-420">
                                  {promo.code} applied (10% off materials)
                                </span>
                                <button type="button" onClick={() => {
                        clearPromoCode();
                        setPromoInput('');
                        try {
                          sessionStorage.removeItem('promo_hint');
                        } catch {}
                      }} className="text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-200 underline ml-1" data-id="element-421">
                                  Remove
                                </button>
                              </div>
                              <span className="font-extrabold text-base text-amber-600 dark:text-amber-400" data-id="element-422">
                                −${promo.discount.toFixed(2)}
                              </span>
                            </div> : !showPromoInput ? <button type="button" onClick={() => setShowPromoInput(true)} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors underline" data-id="element-423">
                              Have a promo code?
                            </button> : <div className="space-y-1.5" data-id="element-424">
                              <div className="flex gap-2" data-id="element-425">
                                <input type="text" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const ok = applyPromoCode(promoInput);
                          if (ok) {
                            try {
                              sessionStorage.removeItem('promo_hint');
                            } catch {}
                          }
                        }
                      }} placeholder="Enter code (e.g. FIRST10)" className="flex-1 px-3 py-2 bg-white dark:bg-[#3a3938] border border-[#e5e5e5] dark:border-white/15 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 text-sm font-bold uppercase tracking-wider focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:outline-none" data-id="element-426" />
                                <button type="button" onClick={() => {
                        const ok = applyPromoCode(promoInput);
                        if (ok) {
                          try {
                            sessionStorage.removeItem('promo_hint');
                          } catch {}
                        }
                      }} className="px-4 py-2 bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white rounded-lg font-bold text-sm border border-transparent dark:border-white/10 transition-colors" data-id="element-427">
                                  Apply
                                </button>
                              </div>
                              {promo.error && <p className="text-xs text-red-500 dark:text-red-400 font-medium px-1" data-id="element-428">
                                  {promo.error}
                                </p>}
                            </div>}
                        </div>

                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg backdrop-blur-sm" data-id="element-429">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-430" />
                            <p className="text-sm text-stone-700 dark:text-stone-300 font-medium" data-id="element-431">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        <div className="flex justify-between items-center" data-id="element-432">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-433">
                            Delivery Fee
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-434">
                            {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-435" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-436">
                                Custom Quote
                              </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-437">
                                FREE
                              </span> : deliveryFee > 0 ? <>
                                ${totalDeliveryFee.toFixed(2)}
                                {deliveryCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-438">
                                    ({deliveryCount} × ${deliveryFee.toFixed(2)}
                                    )
                                  </span>}
                              </> : <span className="text-stone-500 text-sm font-medium" data-id="element-439">
                                Enter address above
                              </span>}
                          </span>
                        </div>

                        {/* Saturday Surcharge Row */}
                        {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-440">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-441">
                              Saturday Delivery
                              <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-442">
                                (weekend surcharge)
                              </span>
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-443">
                              ${saturdaySurcharge.toFixed(2)}
                            </span>
                          </div>}

                        {/* Sales Tax Row — WA combined ~10.1% on materials */}
                        <div className="flex justify-between items-center" data-id="element-444">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-445">
                            Sales Tax
                            <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-446">
                              (est. {(TAX_RATE * 100).toFixed(1)}%)
                            </span>
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-447">
                            ${estimatedTax.toFixed(2)}
                          </span>
                        </div>

                        <div className="mt-2 p-5 rounded-2xl bg-[#f0f4ed] dark:bg-emerald-500/[0.06] border border-[#d4ddcf] dark:border-emerald-500/20 flex justify-between items-end" data-id="element-448">
                          <div className="flex flex-col" data-id="element-449">
                            <span className="text-xs font-bold text-[#6e8c6a] dark:text-emerald-300 uppercase tracking-wider mb-1" data-id="element-450">
                              You pay on delivery
                            </span>
                            <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-451">
                              Total Estimate
                            </span>
                          </div>
                          <span className="font-heading text-3xl sm:text-4xl font-extrabold text-[#006241] dark:text-emerald-300" data-id="element-452">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {(() => {
                const cartReady = isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !!instructions.trim();
                const firstMissing = !isAddressValid ? 'a delivery address' : !isNameValid ? 'your name' : !isPhoneValid ? 'your phone number' : !isEmailValid ? 'your email' : !isDateValid ? 'a delivery date' : !deliveryTime ? 'a time window' : !instructions.trim() ? 'delivery instructions (where to drop the load)' : null;
                return <div className="space-y-4" data-id="element-453">
                            {/* "Half-active" pattern: when required
                              fields are missing the button stays
                              clickable but wears a darker, muted green.
                              Tapping it runs the validation guard
                              (scrolls + focuses the first missing
                              field, which gets a green highlight
                              ring). Feels more helpful than a hard-
                              disabled gray button — the customer can
                              still tap it and is taken straight to
                              what they need to fix. */}
                            <button type="submit" aria-disabled={!cartReady} className={`w-full flex items-center justify-center py-5 rounded-xl font-extrabold text-lg transition-all group text-white cursor-pointer ${cartReady ? 'bg-[#059F40] hover:bg-[#048F3A] shadow-[0_0_20px_rgba(5,159,64,0.3)] hover:shadow-[0_0_25px_rgba(5,159,64,0.5)]' : 'bg-[#0e4a2a] hover:bg-[#0e5a32] shadow-none'}`} data-id="element-454">
                              Proceed to Checkout
                              <ArrowRight className={`w-6 h-6 ml-2 transition-transform ${cartReady ? 'group-hover:translate-x-1' : ''}`} data-id="element-455" />
                            </button>
                            {!cartReady && firstMissing && <p className="text-center text-sm font-semibold text-amber-600 dark:text-amber-400 -mt-1" data-id="element-456">
                                Add {firstMissing} to continue
                              </p>}
                            <div className="grid grid-cols-2 gap-4" data-id="element-457">
                              <button type="button" onClick={handleContinueShopping} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/10 bg-white dark:bg-white/5 backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/10 transition-colors" data-id="element-458">
                                Add More
                              </button>
                              <button type="button" onClick={() => {
                      const cartText = items.map(item => {
                        const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                        const yardsLabel = bumped ? `${item.requestedQuantity} yds material (billed ${item.quantity})` : `${item.quantity} yds`;
                        return `${yardsLabel} - ${item.product} (${item.pricePerYard}/yd)`;
                      }).join('%0D%0A');
                      const yardsLine = yardsBilledExtra > 0 ? `Material Yards: ${materialYards}%0D%0ABilled Yards: ${totalYards} (3 yd order min)` : `Total Yards: ${totalYards}`;
                      const mailtoUrl = `mailto:?subject=253 Gravel - My Cart Summary&body=Here is my cart summary from 253 Gravel:%0D%0A%0D%0A${cartText}%0D%0A%0D%0A${yardsLine}%0D%0AMaterials Total: ${totalPrice.toFixed(2)}%0D%0A%0D%0AView materials at: https://253gravel.com`;
                      window.location.href = mailtoUrl;
                    }} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/10 bg-white dark:bg-white/5 backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/10 transition-colors flex items-center justify-center" data-id="element-459">
                                <Mail className="w-4 h-4 mr-2" data-id="element-460" />
                                Email Cart
                              </button>
                            </div>
                          </div>;
              })()}
                      {/* "Need more than 12 yards or a custom amount?" line
                       removed — customers can already add multiple loads
                       or custom yardage directly from the product selector,
                       so the call-us message was misleading. */}
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto relative" data-id="element-461">
                  {/* Review header */}
                  <div className="sticky top-0 z-[60] bg-[#006241] dark:bg-[#0C2809]/95 backdrop-blur-md border-b border-[#006241] dark:border-white/10 px-8 py-4 shadow-sm" data-id="element-462">
                    <div className="flex items-center gap-2" data-id="element-463">
                      <ClipboardCheck className="w-5 h-5 text-white" data-id="element-464" />
                      <h3 className="font-heading text-base font-extrabold text-white tracking-tight" data-id="element-465">
                        Review your order
                      </h3>
                    </div>
                  </div>

                  <div className="p-8" data-id="element-466">
                    <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-467">
                      {/* Order Summary */}
                      <div data-id="element-468">
                        <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-469">
                          Order Summary
                        </h3>
                        <div className="space-y-3 bg-[#f0f4ed] dark:bg-emerald-500/[0.06] backdrop-blur-sm rounded-2xl border border-[#d4ddcf] dark:border-emerald-500/20 p-5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]" data-id="element-470">
                          {items.map(item => {
                      const materialQty = item.requestedQuantity ?? item.quantity;
                      const isBumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                      return <div key={item.id} className="flex justify-between items-center text-base py-3 border-b border-white/20 dark:border-white/10 last:border-0" data-id="element-471">
                                <div data-id="element-472">
                                  <span className="font-bold text-brand-charcoal dark:text-stone-200" data-id="element-473">
                                    {item.product}
                                  </span>
                                  <span className="text-brand-muted dark:text-stone-400 ml-2 font-medium" data-id="element-474">
                                    × {materialQty} yds
                                    {isBumped && <span className="text-amber-600 dark:text-amber-400" data-id="element-475">
                                        {' '}
                                        (billed {item.quantity} yd · 3 yd min)
                                      </span>}{' '}
                                    · ≈{' '}
                                    {(materialQty * getDensity(item.product)).toFixed(1)}{' '}
                                    tons
                                  </span>
                                </div>
                                <span className="font-extrabold text-brand-charcoal dark:text-white" data-id="element-476">
                                  $
                                  {(() => {
                            const surcharge = (() => {
                              const q = Math.max(3, item.quantity);
                              if (q >= 10) return 1;
                              return 1 + (10 - q) * 0.1;
                            })();
                            return (Math.max(3, item.quantity) * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                          })()}
                                </span>
                              </div>;
                    })}
                          <div className="space-y-3 pt-3" data-id="element-477">
                            <div className="flex justify-between items-center" data-id="element-478">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-479">
                                Subtotal
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-480">
                                ${totalPrice.toFixed(2)}
                              </span>
                            </div>

                            {/* Promo Discount Row */}
                            {promo.code && promo.discount > 0 && <div className="flex justify-between items-center" data-id="element-481">
                                <span className="text-base font-bold text-green-600 dark:text-green-400" data-id="element-482">
                                  Promo {promo.code} (10% off materials)
                                </span>
                                <span className="font-extrabold text-lg text-green-600 dark:text-green-400" data-id="element-483">
                                  −${promo.discount.toFixed(2)}
                                </span>
                              </div>}

                            {/* Delivery Fee Row */}
                            <div className="flex justify-between items-center" data-id="element-484">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-485">
                                Delivery Fee
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-486">
                                {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-487" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-488">
                                    Custom Quote
                                  </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-489">
                                    FREE
                                  </span> : deliveryFee > 0 ? <>
                                    {deliveryCount > 1 && <span className="text-sm text-stone-400 mr-1.5" data-id="element-490">
                                        ({deliveryCount} × $
                                        {deliveryFee.toFixed(2)})
                                      </span>}
                                    ${totalDeliveryFee.toFixed(2)}
                                  </> : <span className="text-stone-500 text-sm font-medium" data-id="element-491">
                                    Based on address
                                  </span>}
                              </span>
                            </div>

                            {/* Saturday Surcharge Row */}
                            {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-492">
                                <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-493">
                                  Saturday Delivery
                                  <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-494">
                                    (weekend surcharge)
                                  </span>
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-495">
                                  {deliveryCount > 1 && <span className="text-sm text-stone-400 mr-1.5" data-id="element-496">
                                      ({deliveryCount} × $
                                      {SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})
                                    </span>}
                                  ${saturdaySurcharge.toFixed(2)}
                                </span>
                              </div>}

                            {/* Sales Tax Row — WA combined ~10.1% on materials */}
                            <div className="flex justify-between items-center" data-id="element-497">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-498">
                                Sales Tax
                                <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-499">
                                  (est. {(TAX_RATE * 100).toFixed(1)}%)
                                </span>
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-500">
                                ${estimatedTax.toFixed(2)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-white/20 dark:border-white/10" data-id="element-501">
                              <span className="font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-502">
                                Total
                              </span>
                              <span className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-503">
                                ${finalTotal.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* First-time buyer FIRST10 nudge — only shown
                         to customers with no prior order on this device.
                         One-tap apply rather than a hidden input. */}
                        {(() => {
                    const isFirstTimeBuyer = typeof window !== 'undefined' && !localStorage.getItem('253gravel_last_order');
                    if (!isFirstTimeBuyer && !promo.code) return null;
                    if (promo.code) {
                      return <div className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-green-50 dark:bg-green-500/10 border border-green-500/30" data-id="element-504">
                                <div className="flex items-center gap-2 min-w-0" data-id="element-505">
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" data-id="element-506" />
                                  <span className="text-sm font-bold text-green-700 dark:text-green-400 truncate" data-id="element-507">
                                    {promo.code} applied (10% off materials)
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 shrink-0" data-id="element-508">
                                  <span className="text-sm font-extrabold text-green-700 dark:text-green-400" data-id="element-509">
                                    −${promo.discount.toFixed(2)}
                                  </span>
                                  <button type="button" onClick={() => {
                            clearPromoCode();
                            setPromoInput('');
                          }} className="text-xs font-bold text-green-700/70 dark:text-green-400/70 hover:text-green-700 dark:hover:text-green-400 underline" data-id="element-510">
                                    Remove
                                  </button>
                                </div>
                              </div>;
                    }
                    return <div className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-400/40 dark:border-amber-500/30" data-id="element-511">
                              <div className="flex items-center gap-2.5 min-w-0" data-id="element-512">
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/90 text-stone-900 shrink-0 font-extrabold text-xs" data-id="element-513">
                                  10%
                                </span>
                                <div className="min-w-0" data-id="element-514">
                                  <p className="text-sm font-bold text-stone-900 dark:text-amber-200 leading-tight" data-id="element-515">
                                    First-time customer?
                                  </p>
                                  <p className="text-xs text-stone-600 dark:text-amber-300/80 leading-tight mt-0.5" data-id="element-516">
                                    Use code{' '}
                                    <span className="font-mono font-bold tracking-wider" data-id="element-517">
                                      FIRST10
                                    </span>{' '}
                                    for 10% off materials.
                                  </p>
                                </div>
                              </div>
                              <button type="button" onClick={() => applyPromoCode('FIRST10')} className="shrink-0 px-3 py-2 bg-stone-900 hover:bg-stone-800 dark:bg-amber-400 dark:hover:bg-amber-300 dark:text-stone-900 text-white rounded-lg text-xs font-extrabold uppercase tracking-wider transition-colors" data-id="element-518">
                                Apply
                              </button>
                            </div>;
                  })()}
                      </div>

                      {/* Delivery Details Summary (Read-Only) */}
                      <div data-id="element-519">
                        <div className="flex items-center justify-between mb-4" data-id="element-520">
                          <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-521">
                            Delivery Details
                          </h3>
                          <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-522">
                            Edit
                          </button>
                        </div>
                        <div className="bg-white dark:bg-white/5 backdrop-blur-sm border border-[#059F40]/25 dark:border-white/10 rounded-xl p-5 space-y-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-523">
                          <div className="flex items-start gap-3" data-id="element-524">
                            <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-525" />
                            <div data-id="element-526">
                              <p className="text-base text-brand-charcoal dark:text-white" data-id="element-527">
                                {addressInput}
                              </p>
                              {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-528">
                                  Zone {deliveryZone}
                                </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-529">
                                  Outside standard zones (Custom Quote)
                                </p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-530">
                            <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-531" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-532">
                              {phone}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-533">
                            <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-534" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-535">
                              {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        }) : 'No date selected'}
                              {deliveryTime && <>
                                  <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-536">
                                    •
                                  </span>
                                  <span className="capitalize" data-id="element-537">
                                    {deliveryTime}
                                  </span>
                                </>}
                            </p>
                          </div>
                          {instructions && <div className="flex items-start gap-3 pt-3 border-t border-white/20 dark:border-white/10" data-id="element-538">
                              <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-539" />
                              <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-540">
                                "{instructions}"
                              </p>
                            </div>}
                        </div>
                      </div>

                      {/* Payment Method + Place Order */}
                      <div data-id="element-541">
                        <div className="flex items-baseline justify-between mb-3" data-id="element-542">
                          <h3 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white tracking-tight" data-id="element-543">
                            Pay on Delivery
                          </h3>
                          <span className="text-xs text-stone-500 dark:text-stone-400 font-medium" data-id="element-544">
                            Online payment coming soon
                          </span>
                        </div>
                        <p className="text-sm text-brand-muted dark:text-stone-400 mb-3" data-id="element-545">
                          Choose how you'll pay your driver, then place your
                          order
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-546">
                          {([{
                      id: 'cash',
                      label: 'Cash',
                      icon: Banknote
                    }, {
                      id: 'card',
                      label: 'Card',
                      icon: CreditCard
                    }] as const).map(opt => {
                      const isActive = payOnDeliverySubMethod === opt.id;
                      const Icon = opt.icon;
                      return <button key={opt.id} type="button" onClick={() => {
                        setPaymentMethod('delivery');
                        setPayOnDeliverySubMethod(opt.id);
                      }} className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${isActive ? 'border-brand-orange bg-brand-orange/10 text-brand-orange' : 'border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 text-brand-charcoal dark:text-stone-200 hover:border-stone-300 dark:hover:border-white/20'}`} aria-pressed={isActive} data-id="element-547">
                                <Icon className="w-4 h-4 shrink-0" data-id="element-548" />
                                <span data-id="element-549">{opt.label}</span>
                              </button>;
                    })}
                        </div>
                        {(() => {
                    const placeOrderDisabled = !isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim() || isProcessingPayment;
                    const placeOrderReady = !placeOrderDisabled;
                    return <motion.button type="submit" disabled={placeOrderDisabled} animate={placeOrderReady ? {
                      boxShadow: ['0 0 0 0 rgba(5,159,64,0.55), 0 4px 14px rgba(5,159,64,0.35)', '0 0 0 10px rgba(5,159,64,0)', '0 0 0 0 rgba(5,159,64,0)']
                    } : {
                      boxShadow: '0 1px 2px rgba(5,159,64,0.15)'
                    }} transition={placeOrderReady ? {
                      duration: 1.8,
                      repeat: Infinity,
                      ease: 'easeOut'
                    } : {
                      duration: 0.2
                    }} whileTap={placeOrderReady ? {
                      scale: 0.98
                    } : undefined} className="relative w-full flex items-center justify-center gap-2 px-4 py-7 rounded-lg text-base font-bold transition-colors bg-[#059F40] hover:bg-[#059F40] text-white disabled:opacity-50 disabled:cursor-not-allowed mb-5" data-id="element-550">
                              {isProcessingPayment ? <>
                                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-551" />
                                  <span data-id="element-552">Processing</span>
                                </> : <>
                                  <Check className="w-5 h-5" strokeWidth={3} data-id="element-553" />
                                  <span data-id="element-554">Place Order</span>
                                </>}
                            </motion.button>;
                  })()}
                        {/* Submission error — surfaces a real, actionable
                         message when an order fails to reach dispatch
                         (network, EmailJS rate-limit, etc.) instead of
                         silently failing. Includes a tap-to-call so the
                         customer can still book over the phone. */}
                        {orderError && !isProcessingPayment && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="mt-3 rounded-xl border border-red-400/60 bg-red-50 dark:bg-red-950/30 p-4" role="alert" aria-live="polite" data-id="element-555">
                            <div className="flex items-start gap-3" data-id="element-556">
                              <Info className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-557" />
                              <div className="flex-1 min-w-0" data-id="element-558">
                                <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1" data-id="element-559">
                                  Couldn't place your order
                                </p>
                                <p className="text-sm text-red-700/90 dark:text-red-200/90 leading-relaxed mb-3" data-id="element-560">
                                  {orderError}
                                </p>
                                <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors" data-id="element-561">
                                  <Phone className="w-3.5 h-3.5" data-id="element-562" />
                                  Call (206) 619-3742
                                </a>
                              </div>
                            </div>
                          </motion.div>}
                      </div>

                      {/* SMS Opt-in */}
                      <div className="p-6 border-b border-stone-100 dark:border-stone-800" data-id="element-563">
                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-564">
                          <div className="relative flex items-center justify-center mt-0.5" data-id="element-565">
                            <input type="checkbox" checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} className="peer sr-only" data-id="element-566" />
                            <div className="w-5 h-5 border-2 border-stone-300 dark:border-stone-600 rounded transition-colors peer-checked:bg-brand-orange peer-checked:border-brand-orange group-hover:border-brand-orange" data-id="element-567"></div>
                            <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-568" />
                          </div>
                          <div data-id="element-569">
                            <span className="text-sm font-medium text-stone-900 dark:text-white block mb-0.5" data-id="element-570">
                              Send me SMS updates about this delivery
                            </span>
                            <span className="text-xs text-stone-500 dark:text-stone-400" data-id="element-571">
                              Get notified when your driver is assigned, en
                              route, and delivered.
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Total reminder */}
                      <div className="px-6 pb-6" data-id="element-572">
                        <div className="flex items-center justify-between text-sm" data-id="element-573">
                          <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-574">
                            Order total
                          </span>
                          <span className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-575">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW — fallback (no summary data) =====
               Safety net: handleSubmitOrder only flips view→'success'
               AFTER setOrderSummaryData runs AND the email was accepted.
               But if it somehow happens (state leak, future refactor),
               show a real message + a way to reach dispatch instead of
               a blank screen behind the "Order Confirmed" header. */}
              {view === 'success' && !orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-576">
                  <div className="flex flex-col items-center text-center space-y-6 max-w-md mx-auto py-8" data-id="element-577">
                    <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center" data-id="element-578">
                      <Info className="w-10 h-10 text-amber-600 dark:text-amber-400" data-id="element-579" />
                    </div>
                    <div data-id="element-580">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-581">
                        We may have lost your order details
                      </h3>
                      <p className="text-base text-brand-muted dark:text-stone-300" data-id="element-582">
                        Your confirmation didn't load on this device. If you
                        placed an order, please call us to verify it went
                        through — we'll find it on our end.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 w-full" data-id="element-583">
                      <a href="tel:206-619-3742" className="w-full py-3.5 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-584">
                        <Phone className="w-5 h-5" data-id="element-585" />
                        Call (206) 619-3742
                      </a>
                      <button type="button" onClick={() => {
                  setView('cart');
                  setOrderError('');
                }} className="w-full py-3 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white font-bold rounded-xl transition-colors" data-id="element-586">
                        Back to cart
                      </button>
                    </div>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-587">
                  <div className="flex flex-col items-center text-center mb-8" data-id="element-588">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20
              }} className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 relative" data-id="element-589">
                      <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-590" />
                      <motion.div initial={{
                  scale: 1,
                  opacity: 0.6
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} className="absolute inset-0 rounded-full border-2 border-green-500" data-id="element-591" />
                    </motion.div>
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-592">
                      Order Received!
                    </h3>
                    <p className="text-lg text-brand-muted dark:text-stone-300 mb-4 max-w-md" data-id="element-593">
                      Thank you, {orderSummaryData.name}. We've received your
                      order request for{' '}
                      {orderSummaryData.materialYards ?? orderSummaryData.totalYards}{' '}
                      yards of material
                      {orderSummaryData.yardsBilledExtra > 0 && <span className="text-amber-600 dark:text-amber-400" data-id="element-594">
                          {' '}
                          (billed as {orderSummaryData.totalYards} yd — 3 yd
                          order min)
                        </span>}
                      .
                    </p>
                    <div className="inline-flex items-center gap-2 bg-brand-orange/10 border border-brand-orange/20 px-4 py-2 rounded-full" data-id="element-595">
                      <span className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider" data-id="element-596">
                        Confirmation #
                      </span>
                      <span className="font-mono font-extrabold text-brand-orange" data-id="element-597">
                        {orderSummaryData.orderNumber}
                      </span>
                    </div>
                  </div>

                  {/* Optional drop-site photo — moved here from checkout so
                   the form stays fast. Customer can take their time and
                   we send it to dispatch as a follow-up tied to the
                   order number. */}
                  <div className="mb-6" data-id="element-598">
                    <PostOrderDropSitePhoto orderNumber={orderSummaryData.orderNumber} customerName={orderSummaryData.name} customerEmail={orderSummaryData.email} address={orderSummaryData.addressInput} data-id="element-599" />
                  </div>

                  {/* What happens next — stepped timeline */}
                  <div className="bg-[#e6f5ea] dark:bg-white/5 border border-[#059F40]/25 dark:border-white/10 rounded-2xl p-6 mb-6 text-left" data-id="element-600">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-5 flex items-center gap-2" data-id="element-601">
                      <MessageSquare className="w-5 h-5 text-brand-orange" data-id="element-602" />
                      What happens next?
                    </h4>
                    <div className="space-y-4" data-id="element-603">
                      {[{
                  icon: Send,
                  title: 'SMS confirmation',
                  desc: <>
                              Text confirmation arriving at{' '}
                              <strong className="text-brand-charcoal dark:text-white" data-id="element-604">
                                {formatPhone(orderSummaryData.phone)}
                              </strong>{' '}
                              within a few minutes.
                            </>,
                  active: true
                }, {
                  icon: ClipboardCheck,
                  title: 'Dispatch review',
                  desc: 'Our team verifies your address, materials, and delivery window — usually within 1 business hour.'
                }, {
                  icon: Truck,
                  title: 'Driver dispatched',
                  desc: 'On delivery day, we text you a live tracking link the moment your driver is en route.'
                }].map((step, i) => {
                  const Icon = step.icon;
                  return <div key={i} className="flex gap-4" data-id="element-605">
                            <div className="flex flex-col items-center" data-id="element-606">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${step.active ? 'bg-[#059F40] text-white shadow-[0_0_12px_rgba(5,159,64,0.4)]' : 'bg-stone-200 dark:bg-white/10 text-stone-500 dark:text-stone-400'}`} data-id="element-607">
                                <Icon className="w-4 h-4" data-id="element-608" />
                              </div>
                              {i < 2 && <div className="w-px flex-1 bg-stone-200 dark:bg-white/10 my-1 min-h-[18px]" data-id="element-609" />}
                            </div>
                            <div className="pb-1 flex-1" data-id="element-610">
                              <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-611">
                                {step.title}
                              </p>
                              <p className="text-sm text-brand-muted dark:text-stone-400 mt-0.5 leading-relaxed" data-id="element-612">
                                {step.desc}
                              </p>
                            </div>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Order details card */}
                  <div className="bg-[#e6f5ea] dark:bg-white/5 border border-[#059F40]/25 dark:border-white/10 rounded-2xl p-6 mb-6 text-left" data-id="element-613">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-614">
                      Order Details
                    </h4>
                    <div className="space-y-3 mb-4" data-id="element-615">
                      {orderSummaryData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm font-medium" data-id="element-616">
                          <span className="text-brand-charcoal dark:text-stone-200" data-id="element-617">
                            {item.quantity} yds — {item.product}
                          </span>
                        </div>)}
                    </div>
                    <div className="space-y-2 text-sm font-medium border-t border-stone-200 dark:border-white/10 pt-4 mb-4" data-id="element-618">
                      <div className="flex justify-between" data-id="element-619">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-620">
                          Materials Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-621">
                          ${orderSummaryData.totalPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-622">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-623">
                          Delivery Fee
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-624">
                          {orderSummaryData.deliveryFeeLabel}
                        </span>
                      </div>
                      {orderSummaryData.saturdaySurcharge > 0 && <div className="flex justify-between" data-id="element-625">
                          <span className="text-brand-muted dark:text-stone-400" data-id="element-626">
                            Saturday Delivery
                          </span>
                          <span className="text-brand-charcoal dark:text-white" data-id="element-627">
                            ${orderSummaryData.saturdaySurcharge.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between" data-id="element-628">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-629">
                          Sales Tax
                          <span className="text-xs text-stone-400 ml-1" data-id="element-630">
                            (est. {(TAX_RATE * 100).toFixed(1)}%)
                          </span>
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-631">
                          ${(orderSummaryData.estimatedTax ?? 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200 dark:border-white/10" data-id="element-632">
                        <span className="text-brand-charcoal dark:text-white" data-id="element-633">
                          Estimated Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-634">
                          ${orderSummaryData.finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm font-medium border-t border-stone-200 dark:border-white/10 pt-4" data-id="element-635">
                      <div className="flex items-start gap-3" data-id="element-636">
                        <MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-637" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-638">
                          {orderSummaryData.addressInput}
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-639">
                        <CalendarDays className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-640" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-641">
                          {new Date(orderSummaryData.deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                          •{' '}
                          <span className="capitalize" data-id="element-642">
                            {orderSummaryData.timeLabel}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-643">
                        <Phone className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-644" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-645">
                          {formatPhone(orderSummaryData.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Prep tip link */}
                  <button type="button" onClick={() => {
              handleCloseSuccess();
              navigate('/delivery-prep');
            }} className="w-full flex items-center justify-between bg-[#f0faf3] dark:bg-white/5 border border-[#059F40]/25 dark:border-white/10 rounded-xl p-4 mb-3 hover:bg-[#e6f5ea] dark:hover:bg-white/10 transition-colors text-left" data-id="element-646">
                    <div className="flex items-center gap-3" data-id="element-647">
                      <Info className="w-5 h-5 text-brand-orange shrink-0" data-id="element-648" />
                      <div data-id="element-649">
                        <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-650">
                          How to prep for delivery
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400" data-id="element-651">
                          Driveway clearance, dump zone, what to expect
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-muted" data-id="element-652" />
                  </button>

                  {/* Action button */}
                  <button onClick={handleCloseSuccess} className="w-full bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white py-4 rounded-xl font-extrabold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm mt-4" data-id="element-653">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}